import { isProjectNotInUsersOrganisationError, isUnknownActiveOrganisationError } from '@constants'
import axios, { AxiosError } from 'axios'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { enqueueSnackbar } from '@ui/snackbar'
import { useAppStore } from '@stores'
import useUIStore from 'src/state/stores/ui'

const WRITE_CONFLICT_ERROR_CODE = 429

const PROJECT_LIST_PATH = '/'

/**
 * Send the user back to the project list when the backend refuses a project as
 * belonging to another organisation.
 *
 * Handled here rather than per request because every project-scoped route
 * answers this, so a stale link or a project that moved organisations breaks
 * *all* of a page's requests at once. Without it the editor stays open on a
 * project it can never load, filling the screen with failures.
 *
 * The message keeps a fixed key so the dozens of requests a page fires while
 * loading collapse into one. The redirect is a full reload (rather than
 * routing through the router) since this runs outside React.
 */
const redirectWhenProjectIsOutOfReach = (error: unknown): void => {
  if (!isProjectNotInUsersOrganisationError(error)) {
    return
  }

  // The project list asks for no single project, so it cannot produce this
  // error itself -- but requests already in flight when we arrive there can,
  // and re-navigating would fight whatever the user does next.
  if (window.location.pathname === PROJECT_LIST_PATH) {
    return
  }

  enqueueSnackbar('Kein Zugriff auf dieses Projekt: Es gehört zu einer anderen Organisation.', {
    variant: 'error',
    key: 'project-not-in-users-organisation',
    preventDuplicate: true,
  })
  window.location.href = PROJECT_LIST_PATH
}

/**
 * Forget an organisation choice the backend no longer accepts.
 *
 * The switcher remembers the last organisation in localStorage, and a
 * membership can end while it does -- as can the browser outlive the account
 * that made the choice. Without this the stored guid is sent on every request
 * forever and nothing works; dropping it falls back to the user's default
 * organisation, which is the state a fresh browser would be in.
 *
 * The switch is silent: the user did not do anything wrong and the default
 * organisation is a working state, so there is nothing for them to act on.
 */
const forgetAnActiveOrganisationTheBackendRejects = (error: unknown): void => {
  if (!isUnknownActiveOrganisationError(error)) {
    return
  }

  const { activeOrganisationGuid, setActiveOrganisationGuid } = useAppStore.getState()

  if (activeOrganisationGuid === null) {
    return
  }

  setActiveOrganisationGuid(null)
  queryClient.clear()
}

const handleOrganisationErrors = (error: unknown): void => {
  redirectWhenProjectIsOutOfReach(error)
  forgetAnActiveOrganisationTheBackendRejects(error)
}

const queryCache = new QueryCache({
  onError: handleOrganisationErrors,
})

type InteractionMeta = {
  title?: string
  description?: string
  interactionId?: string
  // Allow any additional metadata fields
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

const mutationCache = new MutationCache({
  onError: handleOrganisationErrors,
  onMutate: (_variables, mutation) => {
    const { mutationKey, meta } = mutation.options
    const typedMeta = (meta || {}) as InteractionMeta
    const { title, description } = typedMeta

    if (!mutationKey || !title || !description) {
      return
    }

    const id = mutation.mutationId.toString()

    useUIStore.getState().addInteraction({
      id,
      mutationKey,
      title,
      description,
      state: 'running',
      errorResponse: null,
      createdAt: new Date().toISOString(),
    })
  },
  onSettled: (_data, error, _variables, _context, mutation) => {
    const meta = (mutation.options.meta || {}) as InteractionMeta
    const { mutationKey } = mutation.options

    const hasRequiredMeta = Boolean(mutationKey) && Boolean(meta.title) && Boolean(meta.description)

    if (!hasRequiredMeta) {
      return
    }

    const newState = error ? 'failed' : 'success'

    let errorResponse: unknown | null = null

    if (error && axios.isAxiosError(error)) {
      const responseData = (error as AxiosError).response?.data
      if (typeof responseData === 'string') {
        try {
          errorResponse = JSON.parse(responseData)
        } catch {
          // Ignore non-JSON string responses
        }
      } else if (responseData && typeof responseData === 'object') {
        errorResponse = responseData
      }
    }

    useUIStore.getState().updateInteraction(mutation.mutationId.toString(), {
      state: newState,
      errorResponse: newState === 'failed' ? errorResponse : null,
    })
  },
})

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: (failureCount: number, error) => {
        const val =
          axios.isAxiosError(error) &&
          (error as AxiosError).response?.status === WRITE_CONFLICT_ERROR_CODE &&
          failureCount <= 3

        return val
      },
    },
  },
})

export default queryClient
